import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowRight, PlayCircle, ClipboardCheck, FileText, Lock, Check, X } from "lucide-react";
import { PublicAmbient, KineticHeading, Reveal, DonkeyQuote } from "@/components/public/PublicVisuals";

const EASE = [0.22, 1, 0.36, 1];

const PREVIEW_SECTIONS = [
  "Opportunity Profile", "Three Opportunity Portfolio", "Venture Tournament", "Live Market Evidence",
  "Founder Fit", "Adaptability & Dependency Tests", "Future Paths", "Minimum Loss Limit",
  "First Evidence Plan", "Final Donkey's Verdict",
];

// Selective pose states — a deliberate, settling cycle (never a wobble). Each state brings its own reaction.
const STATES = [
  { k: "idle", thought: "I'm not here to tell you every idea is brilliant." },
  { k: "thinking", thought: "Let's see what the market actually says.", tag: { t: "Researching demand", c: "amber" } },
  { k: "sceptical", thought: "That's an assumption, not evidence.", reject: "“Everyone needs this”" },
  { k: "verdict", thought: "Now we've got something worth testing.", tag: { t: "WORTH TESTING", c: "green" } },
];

function CharacterStage() {
  const reduce = useReducedMotion();
  const [i, setI] = useState(0);
  useEffect(() => {
    if (reduce) return;
    const id = setInterval(() => setI((p) => (p + 1) % STATES.length), 3600);
    return () => clearInterval(id);
  }, [reduce]);
  const s = STATES[i];
  const tagColor = s.tag?.c === "green" ? "border-donkey-green/40 bg-donkey-green/10 text-donkey-green" : "border-donkey-amber/40 bg-donkey-amber/10 text-donkey-amber";

  return (
    <div className="relative rounded-3xl border border-white/10 bg-donkey-bg/50 overflow-hidden p-6 sm:p-8">
      <div className="absolute -right-16 -top-16 w-72 h-72 rounded-full bg-donkey-orange/15 blur-[90px] pointer-events-none" aria-hidden />
      <div className="relative grid md:grid-cols-2 gap-6 items-center">
        {/* the character */}
        <div className="relative h-60 sm:h-72 order-1 md:order-none">
          <div className="absolute left-1/2 bottom-2 -translate-x-1/2 w-56 h-16 rounded-[50%] bg-donkey-orange/25 blur-[40px]" aria-hidden />
          <AnimatePresence initial={false}>
            <motion.img
              key={s.k}
              src={`/mascot/${s.k}.png`}
              alt="The Honest Donkey"
              initial={reduce ? false : { opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={reduce ? undefined : { opacity: 0 }}
              transition={{ duration: 0.55, ease: EASE }}
              className="absolute inset-0 h-full w-full object-contain object-bottom drop-shadow-[0_14px_40px_rgba(0,0,0,0.5)]"
            />
          </AnimatePresence>
          {/* thought fragment */}
          <AnimatePresence mode="wait">
            <motion.div
              key={s.k}
              initial={reduce ? false : { opacity: 0, y: 8, filter: "blur(4px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              exit={reduce ? undefined : { opacity: 0, y: -6 }}
              transition={{ duration: 0.45, ease: EASE }}
              className="absolute top-2 left-0 max-w-[14rem] rounded-2xl rounded-bl-sm border border-donkey-orange/30 bg-donkey-bg/85 backdrop-blur-sm px-3.5 py-2 text-sm italic text-donkey-warm/90"
            >
              “{s.thought}”
            </motion.div>
          </AnimatePresence>
          {/* reaction: tag or rejected claim */}
          <div className="absolute bottom-6 right-0 space-y-2">
            <AnimatePresence mode="wait">
              {s.tag && (
                <motion.span key={`tag-${s.k}`} initial={reduce ? false : { opacity: 0, x: 14 }} animate={{ opacity: 1, x: 0 }} exit={reduce ? undefined : { opacity: 0 }} transition={{ duration: 0.4 }}
                  className={`inline-flex items-center gap-1.5 text-xs rounded-full border px-3 py-1.5 ${tagColor}`}>
                  {s.tag.c === "green" && <Check className="w-3.5 h-3.5" />}{s.tag.t}
                </motion.span>
              )}
              {s.reject && (
                <motion.span key={`rej-${s.k}`} initial={reduce ? false : { opacity: 0, x: 14 }} animate={{ opacity: 1, x: 0 }} exit={reduce ? undefined : { opacity: 0 }} transition={{ duration: 0.4 }}
                  className="inline-flex items-center gap-1.5 text-xs rounded-lg border border-donkey-red/40 bg-donkey-red/5 text-donkey-grey px-3 py-1.5 line-through decoration-donkey-red/70">
                  <X className="w-3.5 h-3.5 text-donkey-red no-underline" />{s.reject}
                </motion.span>
              )}
            </AnimatePresence>
          </div>
          {/* state dots */}
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 flex gap-1.5">
            {STATES.map((_, j) => <span key={j} className={`w-1.5 h-1.5 rounded-full transition-colors ${j === i ? "bg-donkey-orange" : "bg-white/20"}`} />)}
          </div>
        </div>
        {/* intro copy */}
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-[0.2em] text-donkey-orange mb-2">Meet the Donkey — free</p>
          <KineticHeading text="See how it works, before you pay." className="text-3xl sm:text-4xl lg:text-5xl" />
          <p className="mt-4 text-donkey-grey max-w-2xl">
            Explore fictional example verdicts, run a quick free Donkey Check, and preview a paid report — all with no
            personalised AI, no research and no cost.
          </p>
          <DonkeyQuote className="mt-5 max-w-xl" quotes={["I'm not here to tell you every idea is brilliant.", "Your enthusiasm isn't evidence.", "Let's find the problems before they become expensive.", "Sometimes the best business advice is DON'T BUILD IT."]} />
        </div>
      </div>
    </div>
  );
}

export default function MeetTheDonkey() {
  const navigate = useNavigate();
  const [examples, setExamples] = useState([]);
  const [disclaimer, setDisclaimer] = useState("");

  useEffect(() => {
    api.get("/preview/examples").then(({ data }) => {
      setExamples(data.examples);
      setDisclaimer(data.disclaimer);
    });
  }, []);

  return (
    <div className="relative overflow-hidden">
      <PublicAmbient intensity={0.5} />
      <div className="relative z-10 mx-auto max-w-6xl px-5 sm:px-8 py-16">
        <CharacterStage />

        {/* Example verdicts */}
        <section className="mt-14">
          <div className="flex items-center gap-2 mb-2">
            <PlayCircle className="w-5 h-5 text-donkey-orange" />
            <h2 className="font-heading font-semibold text-xl text-donkey-warm">Interactive example verdicts</h2>
          </div>
          <p className="text-sm text-donkey-amber/90 mb-6">{disclaimer}</p>
          <div className="grid md:grid-cols-3 gap-5">
            {examples.map((e, idx) => (
              <Reveal key={e.id} y={30} delay={idx * 0.08}>
                <Link
                  to={`/example/${e.id}`}
                  className="block text-left card-donkey rounded-2xl p-6 h-full hover:-translate-y-1 hover:border-donkey-orange/40 transition-all group"
                  data-testid={`example-card-${e.id}`}
                >
                  <p className="text-[11px] uppercase tracking-[0.15em] text-donkey-grey capitalize">{e.journey}</p>
                  <h3 className="font-heading font-semibold text-lg text-donkey-warm mt-1">{e.persona}</h3>
                  <p className="text-sm text-donkey-grey mt-3 leading-relaxed line-clamp-4">{e.circumstances}</p>
                  <span className="mt-4 inline-flex items-center text-sm text-donkey-orange">
                    See this example <ArrowRight className="w-4 h-4 ml-1.5 group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </section>

        {/* Donkey Check + Paid preview */}
        <section className="mt-14 grid md:grid-cols-2 gap-6">
          <div className="card-donkey rounded-2xl p-7">
            <div className="flex items-center gap-2 mb-3">
              <ClipboardCheck className="w-5 h-5 text-donkey-orange" />
              <h2 className="font-heading font-semibold text-xl text-donkey-warm">Free Donkey Check</h2>
            </div>
            <p className="text-sm text-donkey-grey leading-relaxed">
              Ten quick questions and a fixed, honest readiness indication. No AI, no named business ideas, and no
              market-research claim — just an initial steer.
            </p>
            <Button onClick={() => navigate("/donkey-check")} className="mt-6 bg-donkey-orange text-donkey-black font-semibold" data-testid="start-donkey-check-btn">
              Run the free Donkey Check <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
          <div className="card-donkey rounded-2xl p-7">
            <div className="flex items-center gap-2 mb-3">
              <FileText className="w-5 h-5 text-donkey-orange" />
              <h2 className="font-heading font-semibold text-xl text-donkey-warm">Preview a paid report</h2>
            </div>
            <p className="text-sm text-donkey-grey leading-relaxed mb-4">A complete Donkey's Verdict includes:</p>
            <ul className="grid grid-cols-1 gap-1.5">
              {PREVIEW_SECTIONS.map((s) => (
                <li key={s} className="flex items-center gap-2 text-sm text-donkey-warm/80">
                  <Lock className="w-3.5 h-3.5 text-donkey-grey/60" /> {s}
                </li>
              ))}
            </ul>
            <p className="text-xs text-donkey-grey mt-4">Fictional demonstration content only — not a generated report.</p>
          </div>
        </section>

        {/* CTAs */}
        <section className="mt-16 text-center card-donkey rounded-2xl p-10">
          <p className="text-donkey-warm/80 max-w-2xl mx-auto">
            Your complete Donkey's Verdict and your first month of validation in one plan — the complete adaptive
            interview, three opportunities built around you, live market evidence, and a workspace to test real demand.
          </p>
          <p className="mt-4 font-heading font-extrabold text-4xl text-donkey-orange">£29<span className="text-xl text-donkey-warm/70">/month</span></p>
          <p className="mt-2 text-sm text-donkey-grey">£29 per month. Cancel anytime securely through Stripe.</p>
          <div className="mt-7 flex flex-wrap gap-4 justify-center">
            <Button variant="outline" className="border-donkey-line bg-transparent text-donkey-warm hover:border-donkey-orange/60 hover:bg-white/5 rounded-full px-7 h-12" onClick={() => navigate("/example/start")} data-testid="see-example-btn">
              See an example verdict
            </Button>
            <Button className="bg-donkey-orange text-donkey-black hover:bg-donkey-deeporange font-bold rounded-full px-8 h-12 shadow-[0_4px_20px_rgba(249,115,22,0.3)] hover:-translate-y-0.5 transition-all" onClick={() => navigate("/pricing")} data-testid="get-verdict-btn">
              Start with Prove — £29
            </Button>
          </div>
        </section>
      </div>
    </div>
  );
}
